import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  setDoc,
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject, UploadTaskSnapshot } from 'firebase/storage';
import { db, storage } from '../firebase/firebaseConfig';
import { BoardMember, Program, EventItem, VolunteerApplication, AboutContent, HomeContent, DonationContent, BlogPost } from '../types';

// Content Management
export const contentService = {
  async getContent(contentType: string) {
    const docRef = doc(db, 'content', contentType);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  },

  async getAboutContent(): Promise<AboutContent | null> {
    const docRef = doc(db, 'content', 'about');
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? (docSnap.data() as AboutContent) : null;
  },

  async updateContent(contentType: string, data: any) {
    const docRef = doc(db, 'content', contentType);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    } else {
      // Create document if it doesn't exist
      await setDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    }
  },

  async updateAboutContent(data: AboutContent) {
    const docRef = doc(db, 'content', 'about');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    } else {
      // Create document if it doesn't exist
      await setDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    }
  },

  async getHomeContent(): Promise<HomeContent | null> {
    const docRef = doc(db, 'content', 'home');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data() as HomeContent;
      console.log('📖 Raw Firestore data for home:', {
        hasMissionAudioUrl: !!data.missionAudioUrl,
        missionAudioUrl: data.missionAudioUrl,
        allKeys: Object.keys(data)
      });
      return data;
    }
    return null;
  },

  async updateHomeContent(data: HomeContent) {
    const docRef = doc(db, 'content', 'home');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    } else {
      // Create document if it doesn't exist
      await setDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    }
  }
};

// Board Members
export const boardService = {
  async getBoardMembers(): Promise<BoardMember[]> {
    console.log('🚀 getBoardMembers called');
    try {
      // Get all board members first, then sort client-side
      const allMembersSnapshot = await getDocs(collection(db, 'boardMembers'));
      console.log('🔍 All board members query result:', allMembersSnapshot.size);
      
      if (allMembersSnapshot.size === 0) {
        console.log('⚠️ No board members found at all in collection');
        return [];
      }
      
      const allMembers = allMembersSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as BoardMember) }));
      
      // Sort client-side by order field
      const sortedMembers = allMembers.sort((a, b) => {
        const orderA = (a as any).order || 0;
        const orderB = (b as any).order || 0;
        return orderA - orderB;
      });
      
      console.log('✅ Client-side sorted board members:', sortedMembers.length);
      return sortedMembers;
    } catch (error: any) {
      console.error('❌ Error in getBoardMembers:', error);
      throw error;
    }
  },

  async addBoardMember(memberData: BoardMember) {
    return await addDoc(collection(db, 'boardMembers'), {
      ...memberData,
      createdAt: Timestamp.now()
    });
  },

  async updateBoardMember(id: string, data: Partial<BoardMember>) {
    const docRef = doc(db, 'boardMembers', id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now()
    });
  },

  async deleteBoardMember(id: string) {
    await deleteDoc(doc(db, 'boardMembers', id));
  }
};

// Programs
export const programService = {
  async getAllPrograms(): Promise<Program[]> {
    console.log('🚀 getAllPrograms called (admin - all programs)');
    try {
      const allProgramsSnapshot = await getDocs(collection(db, 'programs'));
      console.log('🔍 All programs query result:', allProgramsSnapshot.size);
      return allProgramsSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as Program) }));
    } catch (error: any) {
      console.error('❌ Error in getAllPrograms:', error);
      throw error;
    }
  },

  async getPrograms(): Promise<Program[]> {
    console.log('🚀 getPrograms called');
    try {
      console.log('📊 Attempting to query all programs...');
      // First try to get all programs to debug
      const allProgramsSnapshot = await getDocs(collection(db, 'programs'));
      console.log('🔍 All programs query result:', allProgramsSnapshot.size);
      
      if (allProgramsSnapshot.size === 0) {
        console.log('⚠️ No programs found at all in collection');
        return [];
      }
      
      allProgramsSnapshot.forEach(doc => {
        const data = doc.data();
        console.log('  Program:', doc.id, '- Title:', data.title, '- isActive:', data.isActive, '- type:', typeof data.isActive);
      });
      
      // Filter client-side since Firestore where() might have issues
      const allPrograms = allProgramsSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as Program) }));
      const activePrograms = allPrograms.filter(p => p.isActive === true);
      console.log('✅ Client-side filtered active programs:', activePrograms.length);
      
      return activePrograms;
    } catch (error: any) {
      console.error('❌ Error in getPrograms:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Full error:', error);
      throw error;
    }
  },

  async getProgramById(id: string): Promise<Program | null> {
    const docRef = doc(db, 'programs', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...(docSnap.data() as Program) };
    }
    return null;
  },

  async addProgram(programData: Program) {
    return await addDoc(collection(db, 'programs'), {
      ...programData,
      isActive: true,
      createdAt: Timestamp.now()
    });
  },

  async updateProgram(id: string, data: Partial<Program>) {
    const docRef = doc(db, 'programs', id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now()
    });
  }
};

// Events
export const eventService = {
  async getAllEvents(): Promise<EventItem[]> {
    console.log('🚀 getAllEvents called (admin - all events)');
    try {
      const allEventsSnapshot = await getDocs(collection(db, 'events'));
      console.log('🔍 All events query result:', allEventsSnapshot.size);
      const allEvents = allEventsSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as EventItem) }));
      // Sort by date descending for admin view
      return allEvents.sort((a, b) => {
        const dateA = a.date instanceof Timestamp ? a.date : Timestamp.fromDate(new Date(a.date as any));
        const dateB = b.date instanceof Timestamp ? b.date : Timestamp.fromDate(new Date(b.date as any));
        return dateB.toMillis() - dateA.toMillis();
      });
    } catch (error: any) {
      console.error('❌ Error in getAllEvents:', error);
      throw error;
    }
  },

  async getUpcomingEvents(): Promise<EventItem[]> {
    console.log('🚀 getUpcomingEvents called');
    try {
      // Get all events first, then filter client-side
      const allEventsSnapshot = await getDocs(collection(db, 'events'));
      console.log('🔍 All events query result:', allEventsSnapshot.size);
      
      if (allEventsSnapshot.size === 0) {
        console.log('⚠️ No events found at all in collection');
        return [];
      }
      
      const now = Timestamp.now();
      const allEvents = allEventsSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as EventItem) }));
      
      // Filter client-side for upcoming events
      const upcomingEvents = allEvents.filter(e => {
        const eventDate = e.date instanceof Timestamp ? e.date : Timestamp.fromDate(new Date(e.date as any));
        return eventDate >= now;
      }).sort((a, b) => {
        const dateA = a.date instanceof Timestamp ? a.date : Timestamp.fromDate(new Date(a.date as any));
        const dateB = b.date instanceof Timestamp ? b.date : Timestamp.fromDate(new Date(b.date as any));
        return dateA.toMillis() - dateB.toMillis();
      });
      
      console.log('✅ Client-side filtered upcoming events:', upcomingEvents.length);
      return upcomingEvents;
    } catch (error: any) {
      console.error('❌ Error in getUpcomingEvents:', error);
      throw error;
    }
  },

  async addEvent(eventData: EventItem) {
    return await addDoc(collection(db, 'events'), {
      ...eventData,
      createdAt: Timestamp.now()
    });
  }
};

// Applications
export const applicationService = {
  async submitApplication(type: string, applicationData: VolunteerApplication) {
    return await addDoc(collection(db, 'applications', type), {
      ...applicationData,
      submittedAt: Timestamp.now(),
      status: 'pending'
    });
  },

  async getApplications(type: string) {
    const querySnapshot = await getDocs(
      query(collection(db, 'applications', type), orderBy('submittedAt', 'desc'))
    );
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async updateApplicationStatus(type: string, applicationId: string, status: 'pending' | 'reviewed' | 'accepted' | 'rejected') {
    const docRef = doc(db, 'applications', type, applicationId);
    return await updateDoc(docRef, { status });
  }
};

// File Upload
export const uploadService = {
  async uploadFile(
    file: File, 
    path: string, 
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const storageRef = ref(storage, path);
    
    // Use resumable upload for better progress tracking and error handling
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    return new Promise((resolve, reject) => {
      // Set a timeout (5 minutes for large files)
      const timeout = setTimeout(() => {
        uploadTask.cancel();
        reject(new Error('Upload timeout: File took too long to upload. Please try a smaller file or check your connection.'));
      }, 5 * 60 * 1000); // 5 minutes
      
      uploadTask.on(
        'state_changed',
        (snapshot: UploadTaskSnapshot) => {
          // Calculate progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress(progress);
          }
          console.log(`Upload progress: ${progress.toFixed(1)}%`);
        },
        (error) => {
          clearTimeout(timeout);
          console.error('Upload error:', error);
          reject(error);
        },
        async () => {
          try {
            clearTimeout(timeout);
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('File uploaded successfully:', downloadURL);
            resolve(downloadURL);
          } catch (error) {
            clearTimeout(timeout);
            console.error('Error getting download URL:', error);
            reject(error);
          }
        }
      );
    });
  },

  async deleteFile(path: string) {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  }
};

// Contact & Newsletter
export const contactService = {
  async submitContactForm(formData: any) {
    return await addDoc(collection(db, 'contactSubmissions'), {
      ...formData,
      submittedAt: Timestamp.now()
    });
  },

  async getContactSubmissions() {
    const querySnapshot = await getDocs(
      query(collection(db, 'contactSubmissions'), orderBy('submittedAt', 'desc'))
    );
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async subscribeNewsletter(email: string) {
    return await addDoc(collection(db, 'newsletterSubscriptions'), {
      email,
      subscribedAt: Timestamp.now(),
      isActive: true
    });
  }
};

// Donation Management
export const donationService = {
  async getDonationContent(): Promise<DonationContent | null> {
    const docRef = doc(db, 'content', 'donation');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as DonationContent;
    }
    return null;
  },

  async updateDonationContent(data: DonationContent) {
    const docRef = doc(db, 'content', 'donation');
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      await updateDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    } else {
      await setDoc(docRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
    }
  }
};

// Blog Posts
export const blogService = {
  async getAllBlogPosts(): Promise<BlogPost[]> {
    try {
      const querySnapshot = await getDocs(
        query(collection(db, 'blogPosts'), orderBy('publishedAt', 'desc'))
      );
      return querySnapshot.docs.map(d => ({ id: d.id, ...(d.data() as BlogPost) }));
    } catch (error: any) {
      console.error('❌ Error in getAllBlogPosts:', error);
      throw error;
    }
  },

  async getBlogPosts(): Promise<BlogPost[]> {
    try {
      // Get all posts, filter published ones client-side
      const allPostsSnapshot = await getDocs(
        query(collection(db, 'blogPosts'), orderBy('publishedAt', 'desc'))
      );
      const allPosts = allPostsSnapshot.docs.map(d => ({ id: d.id, ...(d.data() as BlogPost) }));
      // Filter to only published posts
      return allPosts.filter(post => post.isPublished !== false);
    } catch (error: any) {
      console.error('❌ Error in getBlogPosts:', error);
      throw error;
    }
  },

  async getBlogPost(id: string): Promise<BlogPost | null> {
    const docRef = doc(db, 'blogPosts', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...(docSnap.data() as BlogPost) };
    }
    return null;
  },

  async createBlogPost(postData: BlogPost) {
    return await addDoc(collection(db, 'blogPosts'), {
      ...postData,
      isPublished: postData.isPublished !== false,
      publishedAt: postData.publishedAt || Timestamp.now(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
  },

  async updateBlogPost(id: string, data: Partial<BlogPost>) {
    const docRef = doc(db, 'blogPosts', id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now()
    });
  },

  async deleteBlogPost(id: string) {
    await deleteDoc(doc(db, 'blogPosts', id));
  }
};
